import {
    ChangeDetectionStrategy,
    Component,
    effect,
    ElementRef,
    input,
    linkedSignal,
    signal,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { StateService } from "../../../services/state.service";
import { TrailCatalogComponent } from "./trail.catalog.component";
import { TrailCelebrationComponent } from "./trail.celebration.component";
import { TrailExperienceListComponent } from "./trail.experience.list.component";
import { TrailExperienceSubmitComponent } from "./trail.experience.submit.component";
import { TrailLabels } from "./trail.labels";
import { TrailProgressComponent, TrailSubmitExperience } from "./trail.progress.component";
import { TrailRefreshButtonComponent } from "./trail.refresh.button.component";

function parseSearchNumber(raw: string | undefined): number | undefined {
    if (raw == null || String(raw).trim() === "") return undefined;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
}

@TypeManagerDecorator("s25-ng-trail-student-page")
@Component({
    selector: "s25-ng-trail-student-page",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TrailCatalogComponent,
        TrailCelebrationComponent,
        TrailExperienceListComponent,
        TrailExperienceSubmitComponent,
        TrailProgressComponent,
        TrailRefreshButtonComponent,
    ],
    host: { class: "trail-student-page" },
    template: `
        <div class="trail-student-page__body">
            <header class="trail-student-page__header">
                <h1>{{ labels.MY_PATHWAYS }}</h1>
                <s25-ng-trail-refresh-button (refreshed)="onRefreshed()" />
            </header>

            <s25-ng-trail-celebration [reloadToken]="pageReload()" (acknowledged)="onRefreshed()" />

            <s25-ng-trail-catalog
                [reloadToken]="pageReload()"
                (cardSelected)="onCardSelected($event)"
                (pathwaysChanged)="onRefreshed()"
            />

            <section class="trail-student-page__experiences">
                <h2>{{ labels.MY_EXPERIENCES }}</h2>
                <s25-ng-trail-experience-list [reloadToken]="pageReload()" />
            </section>

            @if (selectedDefnId(); as defnId) {
                <section class="trail-student-page__progress" [attr.aria-label]="labels.PROGRESS">
                    <h2>{{ labels.PROGRESS }}</h2>
                    <s25-ng-trail-progress
                        [defnId]="defnId"
                        [reloadToken]="pageReload()"
                        (submitExperience)="onSubmitExperience($event)"
                    />
                </section>
            }

            @if (submittingRequirementId(); as requirementId) {
                <section class="trail-student-page__experience" data-id="experience-submit-section" #experienceSection>
                    <s25-ng-trail-experience-submit
                        [requirementId]="requirementId"
                        [defnId]="submittingDefnId()"
                        [trailId]="submittingTrailId()"
                        (closed)="submittingRequirementId.set(undefined)"
                        (submitted)="onExperienceSubmitted()"
                    />
                </section>
            }
        </div>
    `,
    styles: `
        :host {
            display: block;
        }

        .trail-student-page__body {
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
        }

        .trail-student-page__header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 1rem;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid var(--color--border);
        }

        .trail-student-page__header h1 {
            margin: 0;
            color: var(--color--text-strong);
        }

        .trail-student-page__progress,
        .trail-student-page__experience,
        .trail-student-page__experiences {
            margin-top: 2rem;
        }

        .trail-student-page__experiences h2 {
            margin: 0 0 0.75rem;
            font-size: 1.15rem;
        }

        .trail-student-page__progress h2 {
            margin: 0 0 0.75rem;
            font-size: 1.15rem;
        }
    `,
})
export class TrailStudentPageComponent {
    readonly labels = TrailLabels;
    readonly defnId = input<number | undefined>(parseSearchNumber(StateService.getSearch().defnId));
    readonly selectedDefnId = linkedSignal(() => this.defnId());
    readonly submittingRequirementId = signal<number | undefined>(undefined);
    readonly submittingDefnId = signal<number | undefined>(undefined);
    readonly submittingTrailId = signal<number | undefined>(undefined);
    readonly pageReload = signal(0);
    private readonly experienceSection = viewChild<ElementRef<HTMLElement>>("experienceSection");
    private readonly experienceFormScrollToken = signal(0);

    constructor() {
        effect(() => {
            this.experienceFormScrollToken();
            const section = this.experienceSection();
            if (!this.submittingRequirementId() || !section) return;
            section.nativeElement.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }

    onCardSelected(defnId: number): void {
        this.selectedDefnId.set(defnId);
        StateService.setSearch({ defnId });
    }

    onSubmitExperience(detail: TrailSubmitExperience): void {
        this.submittingRequirementId.set(detail.nodeId);
        this.submittingDefnId.set(detail.defnId);
        this.submittingTrailId.set(detail.trailId);
        this.experienceFormScrollToken.update((count) => count + 1);
    }

    onRefreshed(): void {
        this.pageReload.update((count) => count + 1);
    }

    onExperienceSubmitted(): void {
        this.submittingRequirementId.set(undefined);
        this.onRefreshed();
    }
}
